import { makeUrl, fetchText } from './fetchutils';

// A small TTL cache for read-only Plex Media Server GETs.
//
// Without this, every navigation re-fetches everything: going back to a library you just
// left re-pulls each page of it, and re-opening a movie re-runs the metadata request. PMS
// responses for a given URL are stable over a short window, so a brief cache makes
// back-navigation feel instant without ever showing meaningfully stale data.
//
// Responses are stored as text and re-parsed on each hit, so callers can freely mutate
// (and Vue can make reactive) what they get back without corrupting the cached copy.

const TTL_MS = 60000;
const MAX_ENTRIES = 150;

const cache = new Map();

export const clearFetchCache = () => {
  cache.clear();
};

const get = (key) => {
  const entry = cache.get(key);
  if (!entry) {
    return null;
  }

  if (Date.now() - entry.storedAt > TTL_MS) {
    cache.delete(key);
    return null;
  }

  // Refresh LRU position
  cache.delete(key);
  cache.set(key, entry);
  return entry;
};

const set = (key, text) => {
  cache.set(key, { text, storedAt: Date.now() });

  while (cache.size > MAX_ENTRIES) {
    // Map preserves insertion order, so the first key is the least recently used
    const oldest = cache.keys().next().value;
    cache.delete(oldest);
  }
};

export const fetchJsonCached = async (url, queryParams, { headers, ...rest } = {}) => {
  const key = makeUrl(url, queryParams);

  const hit = get(key);
  if (hit) {
    return JSON.parse(hit.text);
  }

  const text = await fetchText(url, queryParams, {
    headers: {
      Accept: 'application/json',
      ...headers,
    },
    ...rest,
  });

  set(key, text);
  return JSON.parse(text);
};

export default fetchJsonCached;
